import React from 'react';
import { Box, Tooltip } from '@mui/material';
import { WindowKey } from '../../services/executionStatsService';
import {
    ATTENTION_SHORT, AttentionBucket, FLOW_ATTRIBUTION, OPERATIONS_BUCKETS, attentionInk, bucketDefinition, count
} from './executionStatsVocabulary';
import { WorkflowRunStats } from './useExecutionSummary';
import { dsVar, ink } from '../../theme/tokens';

interface WorkflowRunHealthProps {
    state: WorkflowRunStats;
    window: WindowKey;
    /** Drawn solid, to match the attention filter the page has applied. */
    highlight?: AttentionBucket | null;
    /** Opens the runs behind a chip. */
    onSelectBucket?: (bucket: AttentionBucket, attribution: 'campaign' | 'flow') => void;
}

// Box, not Typography: styles.css gives every Typography `font-size: inherit
// !important`, which would size this line like the card's body copy.
const muted = { fontSize: '0.6875rem', color: dsVar('fg-muted') };

/** "12 Oct", or "12 Oct 2025" outside the current year. */
export const runDate = (iso: string, now = new Date()): string => {
    const date = new Date(iso);
    return date.toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'short',
        ...(date.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' })
    });
};

/**
 * A workflow's runs on one line: problems first, then when it last ran.
 *
 * A healthy workflow reads as its last run date alone, in grey, so the card's
 * actions stay the strongest thing in its footer. One with failed or stalled
 * runs leads with those as chips that open the runs behind them — the counts
 * are there to be acted on.
 *
 * Counts that come from the flow rather than this workflow's own tagged runs
 * are drawn outlined, not filled: another workflow built on the same flow may
 * own them. Such a workflow has no run date of its own to show.
 */
const WorkflowRunHealth: React.FC<WorkflowRunHealthProps> = ({ state, window, highlight, onSelectBucket }) => {
    if (state.loading && !state.data) {
        return <Box component="span" sx={muted}>Loading runs…</Box>;
    }
    if (state.error || !state.data || !state.attribution) {
        return (
            <Box component="span" sx={muted} title={state.error ?? undefined}>
                Run statistics unavailable
            </Box>
        );
    }

    const attribution = state.attribution;
    const flowWide = attribution === 'flow';
    const { staleAfterHours } = state.data;
    const counts = state.data.counts[window];
    const lastRunAt = state.data.lastRunAt;
    const recent = window === '24h' ? undefined : state.data.counts['24h'];

    // Pending-review runs are not flagged here: the project page lists the
    // decisions they wait on, by case. The drawer still shows them per run.
    const flagged = counts ? OPERATIONS_BUCKETS.filter((bucket) => (counts[bucket] ?? 0) > 0) : [];
    // Of the flagged runs, how many started in the last day: failing now,
    // rather than failed at some point this month.
    const recentFlagged = recent
        ? flagged.reduce((sum, bucket) => sum + (recent[bucket] ?? 0), 0)
        : 0;

    const flowNote = flowWide && (
        <Tooltip title={FLOW_ATTRIBUTION} arrow placement="top" describeChild>
            <Box component="span" sx={{ ...muted, fontStyle: 'italic', cursor: 'help' }}>· all runs of this flow</Box>
        </Tooltip>
    );

    const lastRun = lastRunAt ? (
        <Box component="span" sx={muted} title={new Date(lastRunAt).toLocaleString()}>
            Last run {runDate(lastRunAt)}
        </Box>
    ) : flowWide ? (
        <Tooltip title={FLOW_ATTRIBUTION} arrow placement="top" describeChild>
            <Box component="span" sx={{ ...muted, cursor: 'help' }}>Last run not recorded</Box>
        </Tooltip>
    ) : (
        <Box component="span" sx={muted}>Not run yet</Box>
    );

    if (!flagged.length) return lastRun;

    return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px 6px' }}>
            {flagged.map((bucket) => {
                const tone = attentionInk(bucket);
                const solid = highlight === bucket;
                const value = counts?.[bucket] as number;
                return (
                    <Tooltip
                        key={bucket}
                        title={`${bucketDefinition(bucket, staleAfterHours)}${flowWide ? ` ${FLOW_ATTRIBUTION}` : ''} Click to see these runs.`}
                        arrow
                        placement="top"
                        describeChild
                    >
                        <button
                            aria-pressed={onSelectBucket ? solid : undefined}
                            onClick={(e) => {
                                e.stopPropagation();
                                onSelectBucket?.(bucket, attribution);
                            }}
                            className="rounded-full px-2 py-0.5 text-xs font-semibold cursor-pointer transition-colors"
                            style={{
                                color: solid ? dsVar('on-accent') : ink(tone),
                                backgroundColor: solid ? tone : flowWide ? 'transparent' : `${tone}1A`,
                                border: `1px ${flowWide && !solid ? 'dashed' : 'solid'} ${solid ? tone : `${tone}80`}`
                            }}
                        >
                            {count(value)} {ATTENTION_SHORT[bucket]}
                        </button>
                    </Tooltip>
                );
            })}
            {recentFlagged > 0 && (
                <Tooltip title="Of these, the runs that started in the last 24 hours." arrow placement="top" describeChild>
                    <Box component="span" sx={{ fontSize: '0.6875rem', color: dsVar('fg-soft'), fontWeight: 600, cursor: 'help' }}>
                        · {count(recentFlagged)} in 24h
                    </Box>
                </Tooltip>
            )}
            {flowNote}
            {lastRunAt && (
                <Box component="span" sx={muted} title={new Date(lastRunAt).toLocaleString()}>
                    · last run {runDate(lastRunAt)}
                </Box>
            )}
        </Box>
    );
};

export default WorkflowRunHealth;
